// Inspecteur · the external-party directory + the five legacy party role-views (LEGACY-COMPARISON L8),
// presented as role-filtered views over ONE external_party table (OWNER Issue 1). P2/Phase D polish: migrated
// from the old inline-style AggregateCrud to the shadcn RegisterScreen + Dialog edit (parity with the
// assets/threats registers) — clean columns (no raw UUID), a near-row Bewerken dialog, and partyKind as a
// labelled dropdown. Edit/deactivate now work end-to-end (partner_relationship.update/delete grant, Phase D).
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import type { Column } from "@/components/data-table";
import {
  RegisterScreen, CreateDialog, EditDialog, DeactivateButton, TagBadge, truncate, type Row, type FormField,
} from "./_shared";

const KIND_VALUES = ["controller", "processor", "recipient", "joint_controller", "contact", "other"];

// `kind` undefined → the full directory (Externe partijen). Set → a role-filtered view; the create form
// defaults to that kind. Contacts match on a contact attribute OR kind=contact (a contact isn't a partyKind).
export function PartyRegister({ kind, titleKey }: { kind?: string; titleKey: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  // partyKind labels via i18n (P3); used both as dropdown options and the column value.
  const kindOpts = KIND_VALUES.map((v) => ({ value: v, label: t(`ins.parties.kind.${v}`) }));
  const kindLabel = (v: unknown) => (KIND_VALUES.includes(String(v)) ? t(`ins.parties.kind.${String(v)}`) : String(v ?? "—"));
  const queryKey = `external-parties${kind ? `-${kind}` : ""}`;
  const invalidate = () => qc.invalidateQueries({ queryKey: [queryKey] });
  const [editing, setEditing] = useState<string | null>(null);

  const matches = (r: Row) =>
    !kind ? true
      : kind === "contact"
        ? String(r.partyKind ?? "") === "contact" || !!String(r.contactName ?? "").trim() || !!String(r.contactEmail ?? "").trim()
        : String(r.partyKind ?? "") === kind;

  const fields: FormField[] = [
    { key: "name", label: t("ins.parties.field.name"), required: true },
    { key: "legalName", label: t("ins.parties.field.legalName") },
    { key: "partyKind", label: t("ins.parties.field.kind"), type: "select", options: kindOpts },
    { key: "country", label: t("ins.parties.field.country") },
    { key: "contactName", label: t("ins.parties.field.contactName") },
    { key: "contactEmail", label: t("ins.parties.field.contactEmail") },
    { key: "notes", label: t("ins.parties.field.notes"), type: "textarea" },
  ];

  const columns: Column<Row>[] = [
    { key: "name", header: t("ins.parties.field.name"), render: (r) => <span className="font-medium">{String(r.name ?? "—")}</span> },
    { key: "legalName", header: t("ins.parties.field.legalName"), render: (r) => <span className="text-muted-foreground">{String(r.legalName ?? "—")}</span> },
    { key: "partyKind", header: t("ins.parties.field.kind"), render: (r) => <TagBadge value={kindLabel(r.partyKind)} /> },
    { key: "country", header: t("ins.parties.field.country"), align: "center", render: (r) => String(r.country ?? "—") },
    { key: "contactName", header: t("ins.parties.field.contactName"), render: (r) => truncate(r.contactName) },
  ];

  return (
    <>
      <RegisterScreen
        title={t(titleKey)}
        help={t("help.partners")}
        queryKey={queryKey}
        listFn={() => (api.listExternalParties() as Promise<Row[]>).then((rows) => (Array.isArray(rows) ? rows.filter(matches) : []))}
        columns={columns}
        empty={t("ins.parties.empty")}
        readPerm="partner_relationship.read"
        actions={
          <Can perm="partner_relationship.write">
            <CreateDialog
              title={t("ins.parties.title")}
              fields={fields}
              initial={kind && kind !== "contact" ? { partyKind: kind } : {}}
              onCreate={(i) => api.createExternalParty(i as Parameters<typeof api.createExternalParty>[0])}
              onDone={invalidate}
            />
          </Can>
        }
        rowActions={(r) => {
          const id = String(r.id);
          return (
            <span className="inline-flex items-center gap-1">
              <Can perm="partner_relationship.update"><button type="button" className="text-xs text-primary hover:underline" onClick={() => setEditing(id)}>{t("crud.edit")}</button></Can>
              <Can perm="partner_relationship.delete"><DeactivateButton basePath={TENANT_PATHS.externalParties} id={id} onDone={invalidate} /></Can>
            </span>
          );
        }}
      />
      {editing && (
        <EditDialog
          basePath={TENANT_PATHS.externalParties}
          id={editing}
          title={t("crud.edit")}
          fields={fields}
          onClose={() => setEditing(null)}
          onDone={() => { invalidate(); setEditing(null); }}
        />
      )}
    </>
  );
}

export const ControllersRoute = () => <PartyRegister kind="controller" titleKey="party.controllers" />;
export const ProcessorsRoute = () => <PartyRegister kind="processor" titleKey="party.processors" />;
export const RecipientsRoute = () => <PartyRegister kind="recipient" titleKey="party.recipients" />;
export const JointControllersRoute = () => <PartyRegister kind="joint_controller" titleKey="party.jointControllers" />;
export const ContactsRoute = () => <PartyRegister kind="contact" titleKey="party.contacts" />;
